import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { deletePlace, fetchHalls } from '../../features/admin/placeSlice';
import ConfirmDeleteModal from './models/ConfirmDeleteModal';
import InnerLoading from '../ui/InnerLoading';
import { FaMapMarkerAlt, FaStar, FaEdit, FaTrashAlt, FaEye, FaChartBar, FaFingerprint } from "react-icons/fa";

import { Link } from 'react-router-dom';
import ScrollButton from '../ui/ScrollButton';
import { TbReportAnalytics } from 'react-icons/tb';
import { FiSearch } from 'react-icons/fi';
import { Hall } from '../../types';
import Pagination from '../ui/Pagination';
import { downloadReport } from '../../utils/data';
import DatePicker from 'react-datepicker';


const HallPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { halls, pagination, loading } = useSelector((state: RootState) => state.placeSlice);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const { user } = useSelector((state: RootState) => state.user);

    const [search, setSearch] = useState("")
    const [selectedHallId, setSelectedHallId] = useState<number | null>(null);
    const [selectedHall, setSelectedHall] = useState<Hall | null>(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [checkIn, setCheckIn] = useState<Date | null>(null)
    const [checkOut, setCheckOut] = useState<Date | null>(null)

    useEffect(() => {
        dispatch(fetchHalls({ userId: user?.id, page: 1 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleDelete = async () => {
        if (selectedHallId !== null) {
            await dispatch(deletePlace(selectedHallId));
            dispatch(fetchHalls({ userId: user?.id, page: 1 }));
            setSelectedHallId(null);
        }
    };

    const confirmDelete = (id: number) => {
        setSelectedHallId(id);
        setDeleteModalOpen(true);
    };

    const localHalls = halls.filter((hall: Hall) => {
        const searchValue = search.toLowerCase();

        const matchSearch =
            hall.id.toString().includes(searchValue) ||
            hall.title?.toLowerCase().includes(searchValue) ||
            hall.location?.toLowerCase().includes(searchValue);

        return matchSearch;
    });
    const showReport = (hall: Hall) => {
        setSelectedHall(hall)
        setShowReportModal(true)
    }

    const handleDownloadReport = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!checkIn || !checkOut) return;

        const payload = {
            placeId: selectedHall?.id,
            checkIn: checkIn.toISOString(),
            checkOut: checkOut.toISOString(),
        };

        await downloadReport("Report/ReportForBookings", payload, `${selectedHall?.title}Report.xlsx`);
    };

    return (
        <div>
            <div className='bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6'>
                <h2 className='text-white text-lg font-semibold'>{t("Hall-title")}</h2>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 mb-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">

                <div className="relative flex-1 min-w-[200px]">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder={t("search-hall") as string}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 pr-3 py-2 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
            </div>
            {loading ? (
                <InnerLoading />
            ) : localHalls.length === 0 ? (
                <p className="text-center text-gray-500">{t("no-Halls")}</p>
            ) : (
                <div className=' grid gap-5 bg-white rounded-xl p-4 '>
                    {localHalls.map((hall) => (
                        <div
                            key={hall.id}
                            className="bg-white flex flex-col sm:flex-row border rounded-xl shadow-sm overflow-hidden hover:shadow-md transition"
                        >
                            <div className="sm:w-48 w-full h-48 sm:h-auto">
                                <img
                                    src={hall.images[0]?.url}
                                    alt={hall.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <div className="flex-1 p-4 space-y-2">
                                <div className="flex justify-between items-start flex-wrap gap-2">
                                    <h3 className="text-lg font-bold text-gray-800">{hall.title}</h3>
                                    {hall.discountPercentage > 0 && (
                                        <span className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
                                            -{hall.discountPercentage}%
                                        </span>
                                    )}
                                </div>
                                {hall.placeIDInPOS && <div className="flex items-center gap-1">
                                    <FaFingerprint className=" text-primary" />
                                    <span>{hall.placeIDInPOS?.split("-").slice(-2).join("-")}</span>
                                </div>}
                                <div className="text-sm text-gray-600">
                                    <div className="flex items-center gap-1">
                                        <FaMapMarkerAlt className="text-blue-500" />
                                        <span>{hall.location}</span>
                                    </div>
                                    <div>
                                        {t("price")}: <strong>{hall.price} {hall.currencyCode}</strong> 
                                    </div>
                                    <div className="flex items-center gap-1 text-yellow-500">
                                        <FaStar />
                                        <span>{hall.rate}</span>
                                    </div>
                                    <div className="line-clamp-2">{hall.details}</div>
                                </div>

                                <div className="flex flex-wrap gap-2 pt-2">
                                    <Link to={`/dashboard/place-details/${hall.id}`}
                                        className="flex items-center gap-1 text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                                    >
                                        <FaEye /> {t("show")}
                                    </Link>
                                    <Link to={`/dashboard/place-modal/${hall.id}`}
                                        className="flex items-center gap-1 text-sm px-3 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded"
                                    >

                                        <FaEdit /> {t("edit")}
                                    </Link>
                                    <button
                                        onClick={() => confirmDelete(hall.id)}
                                        className="flex items-center gap-1 text-sm px-3 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded"
                                    >
                                        <FaTrashAlt /> {t("delete")}
                                    </button>
                                    <Link to={`/dashboard/hall-booking-report/${hall.id}`}
                                        className="flex items-center gap-1 text-sm px-3 py-1 bg-primary text-white hover:bg-[#005011] rounded">
                                        <TbReportAnalytics /> {t("books")}
                                    </Link>
                                    <button
                                        onClick={() => showReport(hall)}
                                        className="flex items-center gap-1 text-sm px-3 py-1 text-red-100 bg-[#1d2481] hover:bg-[#000a99] rounded"
                                    >
                                        <FaChartBar /> {t("reports")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    <Pagination
                        currentPage={pagination?.currentPage || 1}
                        lastPage={pagination?.lastPage || 1}
                        onPageChange={(page) => dispatch(fetchHalls({ userId: user?.id, page }))}
                    />
                </div>


            )}

            <div className="flex justify-end w-full pt-6">
                <Link to={`/dashboard/place-modal`}
                    className="bg-primary px-6 py-2 rounded-full text-white hover:bg-darkColor transition">
                    + {t("add-new-Hall")}
                </Link>
            </div>


            <ScrollButton />
            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                messageKey="delete-Hall-confirm"
            />
            {showReportModal && (
                <div
                    className="fixed inset-0 z-50 flex p-5 items-center justify-center bg-black bg-opacity-40"
                    onClick={() => setShowReportModal(false)}
                >
                    <div
                        className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <form onSubmit={handleDownloadReport} className="w-full flex flex-col gap-6">

                            <h2 className="text-lg font-semibold text-gray-800 text-center">
                                {t("report-period")}
                            </h2>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col">

                                    <DatePicker
                                        selected={checkIn}
                                        onChange={(date) => setCheckIn(date)}
                                        placeholderText={t("start-date") as string}
                                        className="px-3 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                                        dateFormat="yyyy-MM-dd"
                                    />
                                </div>
                                <div className="flex flex-col">

                                    <DatePicker
                                        selected={checkOut}
                                        onChange={(date) => setCheckOut(date)}
                                        placeholderText={t("end-date") as string}
                                        className="px-3 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
                                        dateFormat="yyyy-MM-dd"
                                    />
                                </div>
                            </div>

                            {/* زرار التحميل */}
                            <button
                                type="submit"
                                className="w-full bg-primary hover:bg-[#00470f] transition-colors duration-200 rounded-lg text-white font-medium py-2.5 shadow-md"
                            >
                                {t("download-report")}
                            </button>
                        </form>

                    </div>
                </div>
            )}
        </div>
    );
};
export default HallPage;
